/** エリア選択（束）とお題選択（エリアの中）の組み立て。サーバー由来の文字は textContent だけで入れる */
import type { GameState, StatePrompt } from '../../api/types';
import { remainingMs } from '../../core/clock';
import { btn, h, pressable } from './dom';
import { buildBundles, type Bundle, pointsText, tilt, tradableCount } from './logic';
import type { Ticker } from './ticker';

export interface ViewEnv {
  state: GameState;
  hereId: string | null;
  ticker: Ticker;
  /** 手に入った直後で、絵葉書になる動き（㊴のA）を出すお題 */
  fresh: Set<number>;
  /** コンプの動き（㊲）を出すエリア */
  nowAreas: Set<string>;
  /** 束を1つずつ置く動き（㊹） */
  deal: boolean;
  /** このセッションで答えた正解の選択肢（答えの文字はサーバーから来ないため） */
  answers: Map<number, string>;
  /** 写しをくれたチーム名（自分の申し出で交換したときだけ分かる） */
  partners: Map<number, string>;
  openArea(areaId: string): void;
  openExchange(): void;
  openPrompt(p: StatePrompt, b: Bundle): void;
  onQuizWaitEnd(): void;
}

/** 写真の窓の仮の色（撮った写真は端末にも get_state にも無いため。お題ごとに決まった色） */
const SHOTS = [
  'linear-gradient(135deg,#93876A,#4B5560)',
  'linear-gradient(135deg,#A47C6A,#5A4A5E)',
  'linear-gradient(135deg,#7E8C93,#4F5A3E)',
  'linear-gradient(135deg,#8A9A7A,#55613F)',
  'linear-gradient(135deg,#9DBBD3,#7E9A6E)',
];

function stamp(b: Bundle, id: number, flower: boolean): HTMLElement {
  const s = h('span', 'pr-stamp');
  s.style.setProperty('--a', `var(--pr-${flower ? 'qf' : 'st'}-${b.paint})`);
  s.style.setProperty('--r', `${tilt(id).stamp}deg`);
  return s;
}

function postmark(id: number): HTMLElement {
  const p = h('span', 'pr-pm');
  p.style.setProperty('--r', `${tilt(id).pm}deg`);
  return p;
}

// ---------------------------------------------------------------- エリア選択（束）

export function buildSelect(env: ViewEnv): HTMLElement {
  const root = h('div', 'pr-select');
  const s = env.state;
  const door = btn('pr-door', '');
  door.appendChild(h('b', undefined, '交換所'));
  if (s.exchange.my_listing) door.appendChild(h('em', 'pr-tg pr-me', '掲示中'));
  const cnt = h('span', 'pr-dcnt', '交換できる');
  cnt.appendChild(h('b', undefined, String(tradableCount(s))));
  cnt.appendChild(document.createTextNode('件'));
  door.appendChild(cnt);
  door.addEventListener('click', () => env.openExchange());
  root.appendChild(door);

  const piles = h('div', env.deal ? 'pr-piles pr-deal' : 'pr-piles');
  const bundles = buildBundles(s, env.hereId);
  bundles.forEach((b, i) => {
    piles.appendChild(buildPile(b, env, i));
  });
  root.appendChild(piles);
  return root;
}

function buildPile(b: Bundle, env: ViewEnv, i: number): HTMLElement {
  const cls = ['pr-pile'];
  if (b.complete) cls.push('pr-done');
  if (b.complete && env.nowAreas.has(b.area.area_id)) cls.push('pr-now');
  const p = h('button', cls.join(' '), undefined, { type: 'button' });
  p.style.setProperty('--c', b.hex);
  p.style.setProperty('--dx', `${i % 2 ? -90 : 90}px`);
  p.appendChild(h('span', 'pr-s1'));
  p.appendChild(h('span', 'pr-s2'));
  const top = h('span', 'pr-top');
  if (b.here) top.appendChild(h('em', undefined, 'いまいるエリア'));
  top.appendChild(h('b', undefined, b.area.name));
  top.appendChild(h('span', 'pr-num', `${b.held}／${b.total}`));
  top.appendChild(h('i', 'pr-pt', `${b.points}点`));
  top.appendChild(h('i', 'pr-won', pointsText({ points: b.points, complete: true })));
  p.appendChild(top);
  const mk = h('i', 'pr-mk');
  mk.appendChild(h('i', 'pr-tw-h'));
  mk.appendChild(h('i', 'pr-tw-v'));
  mk.appendChild(h('i', 'pr-knot'));
  p.appendChild(mk);
  p.addEventListener('click', () => env.openArea(b.area.area_id));
  return p;
}

// ---------------------------------------------------------------- お題選択（エリアの中）

export function buildArea(b: Bundle, env: ViewEnv, onBack: () => void): HTMLElement {
  const root = h('div', 'pr-co');
  root.appendChild(btn('pr-back', 'エリア選択に戻る', onBack));
  const sec = h('section', 'pr-area');

  const ah = h('div', 'pr-ah');
  ah.style.setProperty('--c', b.hex);
  const an = h('div', 'pr-an');
  an.appendChild(h('b', undefined, b.area.name));
  if (b.here) an.appendChild(h('span', 'pr-here', 'いまいるエリア'));
  ah.appendChild(an);
  const ac = h('div', 'pr-ac');
  ac.appendChild(h('span', 'pr-num', `${b.held}／${b.total}`));
  const pt = h('span', 'pr-pt');
  pt.appendChild(h('b', 'pr-num', String(b.points)));
  pt.appendChild(document.createTextNode(b.complete ? '点 獲得' : '点'));
  ac.appendChild(pt);
  ah.appendChild(ac);
  sec.appendChild(ah);

  if (b.photos.length > 0) {
    const sub = h('h3', 'pr-sub', '写真');
    sub.appendChild(h('span', 'pr-num', `${b.photoHeld}／${b.photos.length}`));
    sec.appendChild(sub);
    const ga = h('div', 'pr-ga');
    for (const p of b.photos) ga.appendChild(photoCard(p, b, env));
    sec.appendChild(ga);
  }
  if (b.quizzes.length > 0) {
    const sub = h('h3', 'pr-sub', 'クイズ');
    sub.appendChild(h('span', 'pr-num', `${b.quizHeld}／${b.quizzes.length}`));
    sec.appendChild(sub);
    const gq = h('div', 'pr-gq');
    for (const p of b.quizzes) gq.appendChild(quizCard(p, b, env));
    sec.appendChild(gq);
  }
  root.appendChild(sec);
  return root;
}

function photoCard(p: StatePrompt, b: Bundle, env: ViewEnv): HTMLElement {
  if (p.held !== null) {
    const cls = ['pr-pa', 'pr-got'];
    if (env.fresh.has(p.prompt_id)) cls.push('pr-new', 'pr-ka');
    const c = h('div', cls.join(' '));
    const ph = h('span', 'pr-ph');
    ph.style.background = SHOTS[Math.abs(p.prompt_id) % SHOTS.length] ?? '';
    c.appendChild(ph);
    c.appendChild(h('b', undefined, p.title));
    if (p.held === 'trade') c.appendChild(h('small', 'pr-from', env.partners.has(p.prompt_id) ? `${env.partners.get(p.prompt_id)}から` : '交換でもらった写し'));
    c.appendChild(stamp(b, p.prompt_id, false));
    c.appendChild(postmark(p.prompt_id));
    return c;
  }
  if (p.blocked) {
    // 撮れない1枚（㊱ 宛名面）。押すと交換所が開く
    const c = h('button', 'pr-blk', undefined, { type: 'button', 'aria-label': `${p.title}（交換で入手可能）。交換所を開く` });
    c.appendChild(h('span', 'pr-slot'));
    c.appendChild(h('span', 'pr-rule', '交換で入手可能'));
    c.appendChild(h('span', 'pr-to', '交換所へ'));
    c.appendChild(h('b', undefined, p.title));
    c.addEventListener('click', () => env.openExchange());
    return c;
  }
  const c = h('div', 'pr-pa pr-can');
  const ph = h('span', 'pr-ph');
  ph.appendChild(h('i', 'pr-more', '詳細を見る'));
  c.appendChild(ph);
  c.appendChild(h('b', undefined, p.title));
  pressable(c, `${p.title}の詳細を見る`, () => env.openPrompt(p, b));
  return c;
}

function quizCard(p: StatePrompt, b: Bundle, env: ViewEnv): HTMLElement {
  if (p.held !== null) {
    const cls = ['pr-qa', 'pr-got'];
    if (env.fresh.has(p.prompt_id)) cls.push('pr-new', 'pr-ka');
    const c = h('div', cls.join(' '));
    c.appendChild(h('b', undefined, p.title));
    const ans = h('span', 'pr-ans');
    const text = env.answers.get(p.prompt_id);
    if (text) {
      ans.appendChild(document.createTextNode('答え'));
      ans.appendChild(h('strong', undefined, text));
    } else {
      ans.appendChild(h('strong', undefined, '正解'));
    }
    c.appendChild(ans);
    c.appendChild(stamp(b, p.prompt_id, true));
    c.appendChild(postmark(p.prompt_id));
    return c;
  }
  const waiting = remainingMs(p.retry_at) > 0;
  const c = h('div', waiting ? 'pr-qa pr-waiting' : 'pr-qa');
  c.appendChild(h('b', undefined, p.title));
  const ans = h('span', 'pr-ans');
  if (waiting && p.retry_at) {
    ans.classList.add('pr-wait');
    const t = h('b');
    ans.appendChild(t);
    ans.appendChild(document.createTextNode(' 後に答えられます'));
    env.ticker.add(t, p.retry_at, () => env.onQuizWaitEnd());
  }
  c.appendChild(ans);
  c.appendChild(h('i', 'pr-more', '詳細を見る'));
  pressable(c, `${p.title}の詳細を見る`, () => env.openPrompt(p, b));
  return c;
}
